/**
 * SCP Big Product Card JavaScript
 *
 * Extends the regular SCP product card functionality for big cards.
 * Reuses the same carousel, quick-add, and variant selection logic.
 */

if (!customElements.get("scp-big-product-card")) {
  class SCPBigProductCard extends HTMLElement {
    constructor() {
      super();

      // Elements
      this.card = this;
      this.carousel = this.querySelector("[data-scp-carousel]");
      this.track = this.querySelector("[data-carousel-track]");
      this.slides = this.querySelectorAll("[data-carousel-slide]");
      this.prevButton = this.querySelector("[data-carousel-prev]");
      this.nextButton = this.querySelector("[data-carousel-next]");
      this.quickAddTrigger = this.querySelector("[data-quick-add-trigger]");
      this.variantOverlay = this.querySelector("[data-variant-overlay]");
      this.overlayClose = this.querySelector("[data-overlay-close]");
      this.mediaElement = this.querySelector(".m-product-card__media");

      // State
      this.currentSlide = 0;
      this.totalSlides = this.slides.length;
      this.isMobile = window.innerWidth < 990;
      this.hoverTimeout = null;
      this.isHoverActive = false;
      this.quickAddInitialized = false;

      // Product data
      this.productData = this.getProductData();

      // Initialize
      this.init();
    }

    init() {
      if (this.carousel && this.totalSlides > 1) {
        this.initCarousel();
      }

      if (this.quickAddTrigger || this.variantOverlay) {
        this.initQuickAdd();
      }

      // Sync height with normal cards on desktop
      this.syncCardHeights();

      // Mobile detection and height sync on resize
      this.handleResize();
      window.addEventListener(
        "resize",
        this.debounce(() => {
          this.handleResize();
          this.syncCardHeights();
        }, 250)
      );
    }

    getProductData() {
      const jsonScript = this.querySelector("[data-product-json]");
      if (jsonScript) {
        try {
          return JSON.parse(jsonScript.textContent);
        } catch (e) {
          return null;
        }
      }
      return null;
    }

    // ============================================
    // Carousel Logic
    // ============================================

    initCarousel() {
      // Navigation buttons
      if (this.prevButton) {
        this.prevButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.prevSlide();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.nextSlide();
        });
      }

      // Touch/swipe support
      this.initTouchEvents();

      // Hover image change (desktop only)
      if (!this.isMobile && this.mediaElement && this.totalSlides > 1) {
        this.mediaElement.addEventListener("mouseenter", () => {
          this.hoverTimeout = setTimeout(() => {
            if (this.currentSlide === 0) {
              this.isHoverActive = true;
              this.goToSlide(1);
            }
          }, 200);
        });

        this.mediaElement.addEventListener("mouseleave", () => {
          clearTimeout(this.hoverTimeout);
          if (this.isHoverActive && this.currentSlide === 1) {
            this.goToSlide(0);
            this.isHoverActive = false;
          }
        });
      }
    }

    initTouchEvents() {
      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      this.track.addEventListener("touchstart", (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
      });

      this.track.addEventListener("touchmove", (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
      });

      this.track.addEventListener("touchend", () => {
        if (!isDragging) return;

        const diff = startX - currentX;
        const threshold = 50;

        if (Math.abs(diff) > threshold) {
          if (diff > 0) {
            this.nextSlide();
          } else {
            this.prevSlide();
          }
        }

        isDragging = false;
      });
    }

    goToSlide(index) {
      if (index < 0 || index >= this.totalSlides) return;

      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
      });

      this.currentSlide = index;

      // Update button states
      if (this.prevButton) {
        this.prevButton.disabled = index === 0;
        this.prevButton.style.opacity = index === 0 ? "0.5" : "1";
      }

      if (this.nextButton) {
        this.nextButton.disabled = index === this.totalSlides - 1;
        this.nextButton.style.opacity =
          index === this.totalSlides - 1 ? "0.5" : "1";
      }
    }

    nextSlide() {
      this.isHoverActive = false;
      const next = (this.currentSlide + 1) % this.totalSlides;
      this.goToSlide(next);
    }

    prevSlide() {
      this.isHoverActive = false;
      const prev =
        (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
      this.goToSlide(prev);
    }

    // ============================================
    // Quick Add Logic
    // ============================================

    initQuickAdd() {
      // Prevent duplicate event listener bindings
      if (this.quickAddInitialized) return;
      this.quickAddInitialized = true;

      // Desktop: show overlay or add directly (mobile check done at click time)
      if (this.quickAddTrigger) {
        this.quickAddTrigger.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Check mobile state at click time, not init time
          if (this.isMobile) return;

          if (!this.productData) return;

          // Single variant: add directly
          if (
            this.productData.variants &&
            this.productData.variants.length === 1
          ) {
            this.addToCart(this.productData.variants[0].id);
          } else {
            // Multiple variants: show overlay
            this.showVariantOverlay();
          }
        });
      }

      // Overlay close
      if (this.overlayClose) {
        this.overlayClose.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.hideVariantOverlay();
        });
      }

      // Size option clicks
      const sizeOptions = this.querySelectorAll("[data-size-option]");
      sizeOptions.forEach((option) => {
        option.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          const variantId = option.dataset.variantId;
          if (
            variantId &&
            !option.classList.contains(
              "scp-product-card__size-option--disabled"
            )
          ) {
            this.addToCart(variantId);
            this.hideVariantOverlay();
          }
        });
      });

      // Click outside overlay to close
      if (this.variantOverlay) {
        this.variantOverlay.addEventListener("click", (e) => {
          if (e.target === this.variantOverlay) {
            this.hideVariantOverlay();
          }
        });
      }
    }

    showVariantOverlay() {
      if (this.variantOverlay) {
        // Update size options based on currently selected color
        this.updateSizeOptionsForSelectedColor();

        this.variantOverlay.classList.add("is-active");
      }
    }

    updateSizeOptionsForSelectedColor() {
      if (!this.productData || !this.productData.variants) return;

      // Get the currently selected color
      const selectedColorSwatch = this.querySelector(
        '.m-product-option--node__label[data-selected="true"]'
      );
      if (!selectedColorSwatch) {
        return;
      }

      const selectedColor = selectedColorSwatch.dataset.value;
      const colorPosition = parseInt(
        selectedColorSwatch.dataset.optionPosition
      );

      // Find the size option position
      let sizePosition = null;
      for (let i = 0; i < this.productData.options.length; i++) {
        const optionName = this.productData.options[i].toLowerCase();
        if (optionName.includes("size") || optionName.includes("tamanho")) {
          sizePosition = i + 1; // Shopify uses 1-based indexing
          break;
        }
      }

      if (!sizePosition) {
        return;
      }

      // Update each size option button with the correct variant ID
      const sizeOptions = this.querySelectorAll("[data-size-option]");
      sizeOptions.forEach((button) => {
        const sizeValue = button.dataset.sizeValue;

        // Find the matching variant for this color + size combination
        const matchingVariant = this.productData.variants.find((variant) => {
          const variantColor =
            colorPosition === 1
              ? variant.option1
              : colorPosition === 2
              ? variant.option2
              : variant.option3;

          const variantSize =
            sizePosition === 1
              ? variant.option1
              : sizePosition === 2
              ? variant.option2
              : variant.option3;

          return variantColor === selectedColor && variantSize === sizeValue;
        });
        if (matchingVariant) {
          // Update the variant ID
          button.dataset.variantId = matchingVariant.id;

          // Update availability
          if (matchingVariant.available) {
            button.classList.remove("scp-product-card__size-option--disabled");
            button.disabled = false;
          } else {
            button.classList.add("scp-product-card__size-option--disabled");
            button.disabled = true;
          }
        } else {
          // No matching variant found - disable the button
          button.classList.add("scp-product-card__size-option--disabled");
          button.disabled = true;
          button.dataset.variantId = "";
        }
      });
    }

    hideVariantOverlay() {
      if (this.variantOverlay) {
        this.variantOverlay.classList.remove("is-active");
      }
    }

    async addToCart(variantId) {
      if (!variantId) return;
      this.card.classList.add("is-loading");

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:grouped-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const cartAddUrl =
          window.MinimogSettings?.routes?.cart_add_url || "/cart/add.js";

        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", 1);
        formData.append("sections", sectionsToBundle);
        formData.append("sections_url", window.location.pathname);

        const response = await fetch(cartAddUrl, {
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          body: formData,
        });

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error(
            "[SCP Big Card] Non-JSON response. Status:",
            response.status,
            "Content-Type:",
            contentType
          );
          throw new Error("Server returned an invalid response. Please refresh and try again.");
        }

        const data = await response.json();

        if (data.status) {
          throw new Error(
            data.description || data.message || "Failed to add to cart"
          );
        }

        if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
            cart: data,
            productVariantId: variantId,
          });
        }

        const cartDrawer = document.querySelector("m-cart-drawer");
        if (cartDrawer) {
          if (window.MinimogTheme?.Notification && window.MinimogStrings?.itemAdded) {
            MinimogTheme.Notification.show({
              target: cartDrawer.querySelector("m-cart-items"),
              method: "prepend",
              type: "success",
              message: MinimogStrings.itemAdded,
              delay: 400,
            });
          }
          cartDrawer.open();
        }

        document.dispatchEvent(
          new CustomEvent("product-ajax:added", {
            detail: { product: data },
          })
        );
      } catch (error) {
        console.error("[SCP Big Card] Add to cart error:", error);
        this.showErrorFeedback(
          error.message || "Failed to add product to cart. Please try again."
        );
      } finally {
        this.card.classList.remove("is-loading");
      }
    }

    showErrorFeedback(
      message = "Failed to add product to cart. Please try again."
    ) {
      console.error("[SCP Big Card] Error feedback:", message);
      if (window.MinimogTheme && window.MinimogTheme.Notification) {
        window.MinimogTheme.Notification.show({
          target: document.body,
          type: "warning",
          message: message,
          delay: 5000,
          sticky: true,
        });
      }
    }

    // ============================================
    // Responsive Handling
    // ============================================

    handleResize() {
      this.isMobile = window.innerWidth < 990;
    }

    // ============================================
    // Height Synchronization
    // ============================================

    syncCardHeights() {
      // Only sync on desktop
      if (window.innerWidth < 768) return;

      // Find a normal (non-big) product card in the same collection
      const normalCard = document.querySelector(
        ".m-collection-products .m-product-card--scp:not(.m-product-card--scp-big) .m-product-card__media"
      );

      if (!normalCard || !this.mediaElement) return;

      // Get the computed height of the normal card
      const normalHeight = normalCard.offsetHeight;

      // Apply the same height to this big card
      if (normalHeight > 0) {
        this.mediaElement.style.height = `${normalHeight}px`;
      }
    }

    // ============================================
    // Utilities
    // ============================================

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }

  customElements.define("scp-big-product-card", SCPBigProductCard);
}

// Global function to sync all big card heights
function syncAllBigCardHeights() {
  if (window.innerWidth < 768) return;

  const normalCard = document.querySelector(
    ".m-collection-products .m-product-card--scp:not(.m-product-card--scp-big) .m-product-card__media"
  );

  if (!normalCard) return;

  const normalHeight = normalCard.offsetHeight;

  if (normalHeight > 0) {
    const bigCardMedias = document.querySelectorAll(
      ".m-collection-products .m-product-card--scp-big .m-product-card__media"
    );

    bigCardMedias.forEach((media) => {
      media.style.height = `${normalHeight}px`;
    });
  }
}

// Sync heights on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  setTimeout(() => {
    syncAllBigCardHeights();
  }, 100);
});

// Re-sync heights on section load (Shopify theme editor)
document.addEventListener("shopify:section:load", () => {
  setTimeout(() => {
    syncAllBigCardHeights();
  }, 100);
});

// Sync on window resize (debounced)
let resizeTimeout;
window.addEventListener("resize", () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    syncAllBigCardHeights();
  }, 250);
});
